import type { JobActivity, JobStatus, JobType, QcResult } from './api';

/** NKS's words for the whiteboard. Mirrors job-status.ts on the API side. */
export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  WAITING_RECEPTION: 'Waiting at reception',
  WAITING_TECHNICIAN: 'Waiting for a technician',
  DIAGNOSIS: 'Diagnosis',
  WAITING_CUSTOMER_APPROVAL: 'Waiting for the customer',
  WAITING_PARTS: 'Waiting for parts',
  WORK_IN_PROGRESS: 'Work in progress',
  QUALITY_CONTROL: 'Quality control',
  READY_FOR_BILLING: 'Ready for billing',
  READY_FOR_COLLECTION: 'Ready for collection',
  COMPLETED: 'Completed',
  CLOSED: 'Closed',
  ON_HOLD: 'On hold',
  WARRANTY_CLAIM: 'Warranty claim',
  INSURANCE_CLAIM: 'Insurance claim',
};

export const JOB_TYPE_LABEL: Record<JobType, string> = {
  SERVICE: 'Service',
  REPAIR: 'Repair',
  WARRANTY: 'Warranty',
  INSURANCE: 'Insurance',
  ACCESSORY: 'Accessory fitting',
  PDI: 'Pre-delivery inspection',
};

export const ACTIVITY_LABEL: Record<JobActivity, string> = {
  DIAGNOSIS: 'Diagnosing',
  REPAIR: 'Repairing',
  QC: 'Checking',
  // Named for what it is, and the UI says it is not billed.
  WAIT: 'Waiting',
};

export const QC_RESULT_LABEL: Record<QcResult, string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  NA: 'N/A',
};

/** Reserved status band from antd-theme.ts, never the brand colour. */
export function jobTone(status: JobStatus): string | undefined {
  switch (status) {
    case 'READY_FOR_COLLECTION':
    case 'COMPLETED':
    case 'CLOSED':
      return 'success';
    case 'WORK_IN_PROGRESS':
    case 'DIAGNOSIS':
    case 'QUALITY_CONTROL':
      return 'processing';
    case 'WAITING_CUSTOMER_APPROVAL':
    case 'WAITING_PARTS':
    case 'ON_HOLD':
      return 'warning';
    case 'READY_FOR_BILLING':
      return 'cyan';
    default:
      return undefined;
  }
}

/** The three activities a technician picks from. WAIT is handled separately. */
export const BILLABLE_ACTIVITIES: readonly JobActivity[] = ['DIAGNOSIS', 'REPAIR', 'QC'];

/**
 * Seconds → "1h 45m", the way a service advisor speaks.
 *
 * Mirrors describeSeconds on the API. Duplicated rather than fetched because a
 * running timer ticks every second and a round trip per tick is absurd.
 */
export function describeSeconds(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${String(h)}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${String(m)}m ${String(s).padStart(2, '0')}s`;
  return `${String(s)}s`;
}
